import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import React from 'react';
import { StrokeMode } from '~/state/atoms/toolsAtoms';

/**
 * A compact toggle-button group, shorter than a field, so it sits in a
 * tray row without setting the row's height.
 */
export const compactToggleGroupSx = {
    flexShrink: 0,
    '& .MuiToggleButton-root': {
        height: 'auto',
        py: 0.5,
        px: 1,
        fontSize: 11,
        lineHeight: 1,
    },
};

interface ToolTrayModeToggleProps {
    value: StrokeMode;
    onChange: (mode: StrokeMode) => void;
    /** Test id stem; the buttons get `${testIdPrefix}-2d` / `-3d`. */
    testIdPrefix: string;
}

/**
 * The 2D/3D scope toggle carried by the SDF stroke tool trays.
 *
 * Which slices a stroke may touch is the same decision in every tool
 * that offers it, so it is presented by one control: a null selection —
 * clicking the already-selected button — is ignored rather than leaving
 * the tool with no scope at all.
 */
export default function ToolTrayModeToggle({
    value,
    onChange,
    testIdPrefix,
}: ToolTrayModeToggleProps) {
    const handleChange = (
        _: React.MouseEvent<HTMLElement>,
        newMode: StrokeMode | null
    ) => {
        if (newMode === null) {
            return;
        }
        onChange(newMode);
    };

    return (
        <ToggleButtonGroup
            color="primary"
            value={value}
            exclusive
            onChange={handleChange}
            size="small"
            sx={compactToggleGroupSx}
        >
            <ToggleButton value="2d" data-testid={`${testIdPrefix}-2d`}>
                2D
            </ToggleButton>
            <ToggleButton value="3d" data-testid={`${testIdPrefix}-3d`}>
                3D
            </ToggleButton>
        </ToggleButtonGroup>
    );
}
